'use client'

import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
}

export function ChatInput({ onSend, disabled, placeholder = 'Ask anything...' }: ChatInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
    }
  }, [value])

  useEffect(() => {
    if (textareaRef.current && !disabled) {
      textareaRef.current.focus()
    }
  }, [disabled])

  const handleSend = () => {
    const trimmed = value.trim()
    if (trimmed && !disabled) {
      onSend(trimmed)
      setValue('')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className='flex items-end gap-3 border-t border-border-default bg-background-subtle p-4 sm:p-5'>
      <div
        className={cn(
          'flex flex-1 items-end',
          'rounded-2xl border border-border-default bg-white',
          'shadow-inner-soft',
          'transition-all duration-200',
          'focus-within:border-brand-cornflower/40 focus-within:ring-2 focus-within:ring-brand-cornflower/20'
        )}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className={cn(
            'max-h-[120px] flex-1 resize-none bg-transparent px-4 py-3',
            'text-sm text-foreground placeholder:text-brand-muted',
            'focus:outline-none',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        />

        {value.length > 200 && (
          <span className='pb-3 pr-3 text-xs text-brand-muted'>{value.length}</span>
        )}
      </div>

      <Button
        onClick={handleSend}
        disabled={!value.trim() || disabled}
        variant='gradient'
        size='icon'
        className='h-11 w-11 shrink-0'
        aria-label='Send message'
      >
        {disabled ? (
          <Icons.loader className='h-5 w-5 animate-spin' strokeWidth={1.5} />
        ) : (
          <Icons.send className='h-5 w-5' strokeWidth={1.5} />
        )}
      </Button>
    </div>
  )
}
